// src/utils/normalizeZipForJSZip.ts
//
// Work around a JSZip bug that rejects otherwise-valid Intralinks archives.
//
// Intralinks writes a ZIP64 end-of-central-directory record + locator on its
// downloads even when nothing in the archive needs ZIP64. Because the classic
// EOCD fields hold real values (not 0xFFFF / 0xFFFFFFFF sentinels), JSZip never
// enters ZIP64 mode, never accounts for those trailing bytes, and concludes
// that data was *prepended* to the file. It then shifts every offset by the
// trailer length, lands mid-record, finds zero entries, and throws:
//   "Corrupted zip or bug: expected N records in central dir, got 0"
// (jszip/lib/zipEntries.js — readEndOfCentral / readCentralDir, v3.10.x).
//
// Fix: when — and only when — that exact layout is present, hand JSZip the
// same file with the trailer bytes removed. Blob.slice() and the File
// constructor reference the original bytes, so this is zero-copy. Any archive
// not matching the layout is returned untouched (the very same object).

const SIG_EOCD = 0x06054b50;        // end of central directory record
const SIG_CD_HEADER = 0x02014b50;   // central directory file header
const SIG_Z64_EOCD = 0x06064b50;    // ZIP64 end of central directory record
const SIG_Z64_LOCATOR = 0x07064b50; // ZIP64 end of central directory locator

const EOCD_LEN = 22;
const MAX_COMMENT_LEN = 0xffff;
const Z64_LOCATOR_LEN = 20;
const Z64_EOCD_MIN_LEN = 56;

async function readU32LE(file: File, offset: number): Promise<number | null> {
  if (offset < 0 || offset + 4 > file.size) return null;
  const buf = await file.slice(offset, offset + 4).arrayBuffer();
  return new DataView(buf).getUint32(0, true);
}

/**
 * Returns `file` unchanged unless it carries a ZIP64 trailer that JSZip
 * misreads as prepended data, in which case a zero-copy File without the
 * trailer is returned. Never throws — on any doubt, the original is returned.
 */
export async function normalizeZipForJSZip(file: File): Promise<File> {
  try {
    if (file.size < EOCD_LEN + Z64_EOCD_MIN_LEN + Z64_LOCATOR_LEN) return file;

    // The EOCD sits within the last 22 + comment(≤65535) bytes. Scan backwards.
    const tailLen = Math.min(file.size, EOCD_LEN + MAX_COMMENT_LEN);
    const tailStart = file.size - tailLen;
    const tail = new DataView(await file.slice(tailStart).arrayBuffer());

    let eocdRel = -1;
    for (let i = tail.byteLength - EOCD_LEN; i >= 0; i--) {
      if (tail.getUint32(i, true) === SIG_EOCD) {
        eocdRel = i;
        break;
      }
    }
    if (eocdRel < 0) return file;

    const eocd = tailStart + eocdRel;
    const totalEntries = tail.getUint16(eocdRel + 10, true);
    const cdSize = tail.getUint32(eocdRel + 12, true);
    const cdOffset = tail.getUint32(eocdRel + 16, true);

    // Sentinel values mean ZIP64 is genuinely required; JSZip handles that
    // layout correctly on its own, so leave it alone.
    if (totalEntries === 0xffff || cdSize === 0xffffffff || cdOffset === 0xffffffff) {
      return file;
    }

    const cdEnd = cdOffset + cdSize;
    const trailerLen = eocd - cdEnd;
    if (trailerLen < Z64_EOCD_MIN_LEN + Z64_LOCATOR_LEN) return file;

    // Require the exact shape: [central dir][ZIP64 EOCD record][ZIP64 locator][EOCD]
    const [cdSig, z64Sig, locSig] = await Promise.all([
      readU32LE(file, cdOffset),
      readU32LE(file, cdEnd),
      readU32LE(file, eocd - Z64_LOCATOR_LEN),
    ]);
    if (cdSig !== SIG_CD_HEADER || z64Sig !== SIG_Z64_EOCD || locSig !== SIG_Z64_LOCATOR) {
      return file;
    }

    const fixed = new File([file.slice(0, cdEnd), file.slice(eocd)], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
    console.log(
      `   🩹 Removed ${trailerLen}-byte ZIP64 trailer that JSZip misreads as prepended data: "${file.name}"`
    );
    return fixed;
  } catch (err) {
    console.warn(`   ⚠️ ZIP trailer check failed for "${file.name}"; using the file as-is.`, err);
    return file;
  }
}
